import React, { useEffect, useState } from "react";
import PrayerBoardBackDesk from "../components/PrayerBoardBackDesk";
import { getToday, getNotes } from "../api";
import IQAMAH from "../pages/main/pic/IQAMAH.mp3";

import {
  todayMMDDYY,
  tomorrowMMDDYY,
  nowPretty,
  todayPretty,
  scheduleMidnightReload,
} from "../utils/date";

/* ---------------------------------------------------------
   ⭐ UNIVERSAL DATE NORMALIZER
--------------------------------------------------------- */
function normalizeDate(input) {
  if (!input) return null;
  input = input.trim();
  const parts = input.split("/");
  if (parts.length !== 3) return null;

  let [m, d, y] = parts;
  m = m.padStart(2, "0");
  d = d.padStart(2, "0");
  if (y.length === 2) y = "20" + y;

  return `${m}/${d}/${y}`;
}

/* ---------------------------------------------------------
   ⭐ FLEXIBLE DATE LOADER — TRIES MULTIPLE FORMATS
--------------------------------------------------------- */
async function getTodayFlexible() {
  const raw = todayMMDDYY();
  const norm = normalizeDate(raw);

  const candidates = new Set([
    raw,
    norm,
    norm ? `${norm.slice(0, 6)}${raw.slice(-2)}` : null,
    raw.replace(/^0/, ""),
    raw.replace(/\/0/, "/"),
    raw.replace(/^0/, "").replace(/\/0/, "/"),
  ]);

  for (const f of candidates) {
    if (!f) continue;
    try {
      const d = await getToday(f);
      if (d && Object.keys(d).length > 0) {
        console.log("✔ Loaded using date format:", f);
        return d;
      }
    } catch (e) {}
  }

  console.warn("⚠ No matching date format found for:", raw);
  return null;
}

export default function BackDesk() {
  const [data, setData] = useState(null);
  const [tData, setTData] = useState(null);
  const [now, setNow] = useState(nowPretty());
  const [today, setToday] = useState(todayPretty());
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  /* ---------------- NOTES ---------------- */
  useEffect(() => {
    getNotes().then(setNotes);
  }, []);

  /* ---------------- TODAY ---------------- */
  useEffect(() => {
    getTodayFlexible()
      .then((d) => setData(d))
      .finally(() => setIsLoading(false));

    const timer = setInterval(() => setNow(nowPretty()), 1000);
    scheduleMidnightReload();
    return () => clearInterval(timer);
  }, []);

  /* ---------------- TOMORROW ---------------- */
  useEffect(() => {
    const normTomorrow = normalizeDate(tomorrowMMDDYY());
    if (!normTomorrow) return;
    getToday(normTomorrow)
      .then(setTData)
      .catch(() => {});
  }, []);

  /* ---------------- REFRESH BEFORE IQAMAH ---------------- */
  function timeToSeconds1(str) {
    if (!str) return null;
    const [h, m] = str.split(":").map(Number);
    return h * 3600 + m * 60;
  }

  useEffect(() => {
    if (!data) return;

    const now = new Date();
    const todaySeconds =
      now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

    const refreshOffsets = {
      fajrIqamah: 10,
      dhuhrIqamah: 11,
      asrIqamah: 12,
      maghribIqamah: 13,
      ishaIqamah: 14,
    };

    Object.entries(refreshOffsets).forEach(([key, offset]) => {
      if (!data[key]) return;

      const targetSeconds = timeToSeconds1(data[key]);
      if (targetSeconds == null) return;

      const delayToIqamah = (targetSeconds - todaySeconds) * 1000;
      if (delayToIqamah > 0) {
        const refreshDelay = delayToIqamah - offset * 1000;
        if (refreshDelay > 0) {
          setTimeout(() => window.location.reload(), refreshDelay);
        }
      }
    });
  }, [data]);

  /* ---------------- BLACKOUT LOGIC ---------------- */
  function timeToSeconds(str) {
    if (!str) return null;
    const [h, m] = str.split(":").map(Number);
    return h * 3600 + m * 60;
  }

  const [blackout, setBlackout] = useState(false);
  const [remaining, setRemaining] = useState(0);

  const durations = {
    fajrIqamah: 20,
    dhuhrIqamah: 20,
    asrIqamah: 15,
    maghribIqamah: 20,
    ishaIqamah: 20,
  };

  useEffect(() => {
    if (!data) return;

    const now = new Date();
    const todaySeconds =
      now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

    Object.entries(durations).forEach(([key, minutes]) => {
      if (!data[key]) return;

      const targetSeconds = timeToSeconds(data[key]);
      if (targetSeconds == null) return;

      const delay = (targetSeconds - todaySeconds) * 1000;

      if (delay > 0) {
        setTimeout(() => {
          const weekday = new Date().toLocaleDateString(undefined, {
            weekday: "long",
          });

          if (key === "dhuhrIqamah" && weekday === "Friday") {
            triggerBlackout(60);
          } else {
            triggerBlackout(minutes);
          }
        }, delay);
      }
    });
  }, [data]);

  function triggerBlackout(minutes) {
    setBlackout(true);
    setRemaining(minutes * 60);

    const interval = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setBlackout(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  function formatTime2(sec) {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(
      2,
      "0",
    )}:${String(s).padStart(2, "0")}`;
  }

  /* ---------------- LOADING ---------------- */
  if (isLoading) return <div>Loading...</div>;

  /* ---------------- NO DATA ---------------- */
  if (!data) {
    return (
      <div style={{ textAlign: "center", padding: 40, fontSize: 40 }}>
        No prayer data available for today.
      </div>
    );
  }

  /* ---------------- MAIN UI ---------------- */
  return (
    <div>
      <PrayerBoardBackDesk data={data} title="Staff View" />

      <div>
        <p
          style={{
            textAlign: "center",
            fontSize: "70px",
            marginTop: "-15px",
            marginBottom: "-20px",
            letterSpacing: "-2px",
          }}
        >
          <span
            style={{ fontFamily: "Space Mono, monospace", fontSize: "140px" }}
          >
            {now}
            {/* 56:56:12 56 */}
          </span>
          <span style={{ letterSpacing: "-5px" }}>| {today}</span>
        </p>

        <div
          style={{
            background: "#000",
            height: "45px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <b>
            <marquee
              direction="left"
              style={{
                fontSize: "45px",
                backgroundColor: "#000",
                color: "white",
              }}
            >
              {notes.length > 0
                ? notes.map((note) => `${note.text}`).join(" | ")
                : "No Notes Available"}
            </marquee>
          </b>
        </div>
      </div>

      {/* ⭐ BLACKOUT OVERLAY ⭐ */}
      {blackout && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 999999,
            background: "black",
            color: "white",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          {/* First 10 minutes */}
          {remaining > 600 && (
            <>
              <div
                style={{
                  fontSize: 180,
                  fontWeight: "bold",
                  marginBottom: 40,
                  color: "#2a3127",
                }}
              >
                SILENCE PLEASE
                {/* 🔇 */}
              </div>
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <div style={{ fontSize: 40, marginBottom: 20 }}>
                Please, Turn off your Phones 📵. Move Forward. Fill first ranks
                first. <br />
                Heels, not toe in line. Shoulder to shoulder, not arms. No
                talking. No whispering.
              </div>
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <div style={{ fontSize: 40, marginBottom: 20 }}>
                Will return in {formatTime2(remaining)}
              </div>
            </>
          )}
          {remaining <= 600 && (
            <>
              <marquee
                direction="up"
                scrollAmount="1" // lower number = slower
                scrollDelay="90" // higher number = slower
                style={{
                  fontSize: 38,
                  color: "yellow",
                  marginBottom: 40,
                  height: 750, // controls visible window
                }}
              >
                1. اللّٰهُ أَكْبَر <br /> Allahu Akbar <br /> "Allah is the
                greatest"
                <br /> <br /> 2. أَسْتَغْفِرُ اللهَ، أَسْتَغْفِرُ اللهَ،
                أَسْتَغْفِرُ اللهَ
                <br />
                Astaghfirullah, Astaghfirullah, Astaghfirullah
                <br />
                "I seek forgiveness from Allah"
                <br /> <br />
                3. اللَّهُمَّ أَعِنِّي عَلَى ذِكْرِكَ، وَشُكْرِكَ، وَحُسْنِ
                عِبَادَتِكَ
                <br /> Allahumma a'inni 'ala dhikrika, wa shukrika, wa husni
                'ibadatika
                <br />
                "O Allah, help me to remember You, to be grateful to You, and to
                worship You in an excellent manner"
                <br /> <br />
                4. ‏ اللَّهُمَّ أَنْتَ السَّلاَمُ وَمِنْكَ السَّلاَمُ
                تَبَارَكْتَ يَا ذَا الْجَلاَلِ وَالإِكْرَامِ
                <br /> Allahumma Antas-Salam wa minkas-salam. Tabarakta ya
                Dhal-jalali wal- ikram. Allahumma Antas Salam Dua
                <br /> “O Allah, You are As-Salam, From You is all peace,
                blessed are You O Possessor of majesty and honour”
                <br />
                <br /> 5. لَا إِلَهَ إِلَّا اللهُ وَحْدَهُ لَا شَرِيكَ لَهُ،
                لَهُ الْمُلْكُ وَلَهُ الْحَمْدُ، وَهُوَ عَلَى كُلِّ شَيءٍ
                قًدِيرٌ‏.‏ اَللَّهُمَّ لَا مَانِعَ لِمَا أَعْطَيتَ، وَلَا
                مُعْطِيَ لِمَا مَنَعْتَ، وَلَا يَنْفَعُ ذَا الْجَدِّ مِنْكَ
                الْجَدُّ
                <br /> Lā ilāha illallāhu wahdahu lā syarika lahu, lahul-mulku
                wa lahul-hamdu, wa Huwa 'alā kulli shay-in Qadir. Allāhumma lā
                māni'a limā a'tayta, wa lā mu'tiya limā mana'ta, wa lā yanfa'u
                zal-jaddi minkal-jaddu. <br />
                <br />
                6. Recite Ayat al-Kursi
                <br /> Hadith: Allah's Messenger (ﷺ) said: "Whoever recites Ayat
                al-Kursi at the end of every obligatory prayer, nothing but
                death will prevent him from entering Paradise." (authentic).
                Sunnah.com reference : Book 2, Hadith 220 English translation :
                Book 2, Hadith 324 Arabic reference : Book 2, Hadith 326
                <br />
                <br />
                7.Recite Sayyidul istighfar:
                <br />
                اللَّهُمَّ أَنْتَ رَبِّي لاَ إِلَهَ إِلاَّ أَنْتَ خَلَقْتَنِي
                وَأَنَا عَبْدُكَ وَأَنَا عَلَى عَهْدِكَ وَوَعْدِكَ مَا
                اسْتَطَعْتُ أَعُوذُ بِكَ مِنْ شَرِّ مَا صَنَعْتُ أَبُوءُ لَكَ
                بِنِعْمَتِكَ عَلَىَّ وَأَبُوءُ لَكَ بِذَنْبِي فَاغْفِرْ لِي
                فَإِنَّهُ لاَ يَغْفِرُ الذُّنُوبَ إِلاَّ أَنْتَ
                <br />
                Allahumma anta Rabbi la ilaha illa anta, Khalaqtani wa ana
                `Abduka, wa ana `ala `ahdika wa wa`dika mastata`tu, A`udhu bika
                min Sharri ma sana`tu, abu'u Laka bini`matika `alaiya, wa abu'u
                laka bidhanbi faghfir lee fa innahu la yaghfiru adhdhunuba illa
                anta.
                <br />
                <br />
                1. اللّٰهُ أَكْبَر <br /> Allahu Akbar <br /> "Allah is the
                greatest"
                <br /> <br /> 2. أَسْتَغْفِرُ اللهَ، أَسْتَغْفِرُ اللهَ،
                أَسْتَغْفِرُ اللهَ
                <br />
                Astaghfirullah, Astaghfirullah, Astaghfirullah
                <br />
                "I seek forgiveness from Allah"
                <br /> <br />
                3. اللَّهُمَّ أَعِنِّي عَلَى ذِكْرِكَ، وَشُكْرِكَ، وَحُسْنِ
                عِبَادَتِكَ
                <br /> Allahumma a'inni 'ala dhikrika, wa shukrika, wa husni
                'ibadatika
                <br />
                "O Allah, help me to remember You, to be grateful to You, and to
                worship You in an excellent manner"
                <br /> <br />
                4. ‏ اللَّهُمَّ أَنْتَ السَّلاَمُ وَمِنْكَ السَّلاَمُ
                تَبَارَكْتَ يَا ذَا الْجَلاَلِ وَالإِكْرَامِ
                <br /> Allahumma Antas-Salam wa minkas-salam. Tabarakta ya
                Dhal-jalali wal- ikram. Allahumma Antas Salam Dua
                <br /> “O Allah, You are As-Salam, From You is all peace,
                blessed are You O Possessor of majesty and honour”
                <br />
                <br /> 5. لَا إِلَهَ إِلَّا اللهُ وَحْدَهُ لَا شَرِيكَ لَهُ،
                لَهُ الْمُلْكُ وَلَهُ الْحَمْدُ، وَهُوَ عَلَى كُلِّ شَيءٍ
                قًدِيرٌ‏.‏ اَللَّهُمَّ لَا مَانِعَ لِمَا أَعْطَيتَ، وَلَا
                مُعْطِيَ لِمَا مَنَعْتَ، وَلَا يَنْفَعُ ذَا الْجَدِّ مِنْكَ
                الْجَدُّ
                <br /> Lā ilāha illallāhu wahdahu lā syarika lahu, lahul-mulku
                wa lahul-hamdu, wa Huwa 'alā kulli shay-in Qadir. Allāhumma lā
                māni'a limā a'tayta, wa lā mu'tiya limā mana'ta, wa lā yanfa'u
                zal-jaddi minkal-jaddu. <br />
                <br />
                6. Recite Ayat al-Kursi
                <br /> Hadith: Allah's Messenger (ﷺ) said: "Whoever recites Ayat
                al-Kursi at the end of every obligatory prayer, nothing but
                death will prevent him from entering Paradise." (authentic).
                Sunnah.com reference : Book 2, Hadith 220 English translation :
                Book 2, Hadith 324 Arabic reference : Book 2, Hadith 326
                <br />
                <br />
                7.Recite Sayyidul istighfar:
                <br />
                اللَّهُمَّ أَنْتَ رَبِّي لاَ إِلَهَ إِلاَّ أَنْتَ خَلَقْتَنِي
                وَأَنَا عَبْدُكَ وَأَنَا عَلَى عَهْدِكَ وَوَعْدِكَ مَا
                اسْتَطَعْتُ أَعُوذُ بِكَ مِنْ شَرِّ مَا صَنَعْتُ أَبُوءُ لَكَ
                بِنِعْمَتِكَ عَلَىَّ وَأَبُوءُ لَكَ بِذَنْبِي فَاغْفِرْ لِي
                فَإِنَّهُ لاَ يَغْفِرُ الذُّنُوبَ إِلاَّ أَنْتَ
                <br />
                Allahumma anta Rabbi la ilaha illa anta, Khalaqtani wa ana
                `Abduka, wa ana `ala `ahdika wa wa`dika mastata`tu, A`udhu bika
                min Sharri ma sana`tu, abu'u Laka bini`matika `alaiya, wa abu'u
                laka bidhanbi faghfir lee fa innahu la yaghfiru adhdhunuba illa
                anta.
              </marquee>

              <div style={{ fontSize: 40, marginBottom: 20 }}>
                Will return in {formatTime2(remaining)}
              </div>
            </>
          )}

          <div style={{ fontSize: 20 }}>
            Application Developer: 845.514.8834 | Message to Suggest
          </div>
        </div>
      )}
    </div>
  );
}
